import { Storage } from 'aws-amplify';

import { type CreateFlightDocumentInput } from '../../../API';
import { logError } from '../../../utils/ErrorLog/ErrorLog';
import {
    type FlightDocument,
    type FlightDocumentType,
    type LocalFlightDocument,
    type SavedFlightDocument,
} from '../../FlightDocuments/FlightDocument';
import { isFlightDocumentSaved } from '../../FlightDocuments/FlightDocument';
import { getLocalFlightDocumentContent } from '../../FlightDocuments/LocalDocumentContent';
import { type FlightLegPhase } from '../../FlightLegs/FlightLeg';
import { getDb } from '../LocalDB';
import { getStandardItemHandler } from '../standardItemHandler';
import { type DbSyncerUpDefinition } from '../syncHelpers';
import { getStandardSyncerFields } from '../syncHelpers';
import { type DbCreated } from '../types';

let counter = 0;

// A sync that outlasts DbSyncer's 29 s guard overlaps the next one, which holds the same documents.
const uploadsInFlight = new Map<string, Promise<UploadResult>>();

/** `purged`: the row was removed locally while its content uploaded. */
type UploadResult = { key: string; purged: boolean };

export const flightDocumentItemFields = `
    id
    deleted
    editableByUserIds
    createdAt
    aircraftId
    flightId
    legId
    squawkId
    deferralId
    releaseToServiceId
    maintenanceRecordId
    contentKey
    type
    phase
    title
    mediaFileId
    _version
`.trim();

async function uploadDocument(document: LocalFlightDocument): Promise<UploadResult> {
    try {
        const content = await getLocalFlightDocumentContent(document.id);
        if (!content) {
            if (document.deleted) {
                // Deleted before its upload by a version that dropped the content but kept the row. The server
                // never had it, and its sync item would fail here on every sync.
                const db = getDb();
                await db.transaction('rw', [db.documents, db.syncItems], async () => {
                    await Promise.all([
                        db.documents.delete(document.id),
                        db.syncItems.delete(`${db.documents.name}:${document.id}`),
                    ]);
                });
            }
            // noinspection ExceptionCaughtLocallyJS
            throw new Error('Cannot find local document content when uploading new document');
        }
        const { default: mime } = await import('mime');
        const suffix = mime.getExtension(content.type) || 'bin';
        const filename = `${document.id}.${suffix}`;
        const expires = new Date();
        expires.setFullYear(expires.getFullYear() + 1);
        const result = (await Storage.put(filename, content, {
            contentType: content.type,
            expires,
        })) as { key: string };
        // Onto the stored row: the copy this sync read can be older, and a purged row must stay gone.
        const db = getDb();
        await db.documents.update(document.id, { contentKey: result.key });
        // Not update()'s count: it is 0 too when the row already holds this key (a repeated upload of the same file).
        const purged = !(await db.documents.get(document.id));

        return { key: result.key, purged };
    } catch (error: any) {
        logError(error.message, 'flightDocuments/upload');
        throw error;
    }
}

const uploadDocumentOnce = async (document: LocalFlightDocument): Promise<UploadResult> => {
    const inFlight = uploadsInFlight.get(document.id);
    if (inFlight) {
        return inFlight;
    }
    const upload = uploadDocument(document).finally(() => uploadsInFlight.delete(document.id));
    uploadsInFlight.set(document.id, upload);

    return upload;
};

export const flightDocumentToDb: DbSyncerUpDefinition = (document: FlightDocument) => {
    const c = ++counter;
    const [commandName, inputType] = document._version
        ? ['updateFlightDocument', 'UpdateFlightDocumentInput']
        : ['createFlightDocument', 'CreateFlightDocumentInput'];
    const input = async (): Promise<CreateFlightDocumentInput> => {
        const { key: contentKey, purged } = isFlightDocumentSaved(document)
            ? { key: document.contentKey, purged: false }
            : await uploadDocumentOnce(document);
        // The upload writes the key onto the stored row. DbSyncer holds this object as the row it sent and
        // compares the stored row against it on reply, so without the key the reply looks stale.
        document.contentKey = contentKey;
        // noinspection UnnecessaryLocalVariableJS
        const input: CreateFlightDocumentInput = {
            id: document.id,
            // A row purged during the upload was deleted locally; this snapshot predates that.
            deleted: document.deleted || purged,
            createdAt: document.createdAt.toISOString(),
            aircraftId: document.aircraftId,
            flightId: document.flightId,
            legId: document.legId,
            squawkId: document.squawkId,
            deferralId: document.deferralId,
            releaseToServiceId: document.releaseToServiceId,
            maintenanceRecordId: document.maintenanceRecordId,
            contentKey,
            type: document.type,
            phase: document.phase,
            title: document.title,
            _version: document._version,
            mediaFileId: document.mediaFileId ?? 'x',
        };

        return input;
    };

    const { mutationName, inputKey, query } = getStandardSyncerFields(commandName, c, flightDocumentItemFields);
    const remoteToLocalTransform = flightDocumentTransform;

    return { query, input, inputKey, inputType, mutationName, remoteToLocalTransform, queryName: 'getFlightDocument' };
};

export const flightDocumentTransform = (input: CreateFlightDocumentInput): SavedFlightDocument => ({
    id: input.id!,
    aircraftId: input.aircraftId,
    createdAt: new Date(input.createdAt!),
    flightId: input.flightId || undefined,
    legId: input.legId || undefined,
    squawkId: input.squawkId || undefined,
    deferralId: input.deferralId || undefined,
    releaseToServiceId: input.releaseToServiceId || undefined,
    maintenanceRecordId: input.maintenanceRecordId || undefined,
    contentKey: input.contentKey,
    type: input.type as FlightDocumentType,
    phase: input.phase as FlightLegPhase,
    deleted: input.deleted,
    title: input.title,
    _version: input._version!,
    editableByUserIds: input.editableByUserIds || [],
    mediaFileId: input.mediaFileId || undefined,
});

export const flightDocumentItemHandler = async (items: Array<CreateFlightDocumentInput & DbCreated>): Promise<void> => {
    const db = getDb();
    await getStandardItemHandler(db.documents, flightDocumentTransform)(items);
    // Only for rows the handler really removed: a row with a queued upload is kept, and so is its content.
    const deletedIds = items.filter((i) => i.deleted).map((i) => i.id);
    const remaining = await db.documents.bulkGet(deletedIds);
    await db.localDocumentContents.bulkDelete(deletedIds.filter((_id, index) => !remaining[index]));
};
